import { ReactNode, useRef, useEffect, useState } from 'react';

/**
 * Viewport-aware lazy section wrapper.
 * Only renders children (triggering React.lazy chunk downloads)
 * when the placeholder enters the viewport vicinity.
 * This prevents framer-motion and other heavy libraries from loading
 * until the user actually scrolls near them.
 */
interface LazySectionProps {
  children: ReactNode;
  /** How far before the viewport to start loading (default: 300px) */
  rootMargin?: string;
  /** Minimum height for the placeholder to avoid layout shift */
  minHeight?: string;
}

export const LazySection = ({
  children,
  rootMargin = '300px',
  minHeight = '100px',
}: LazySectionProps) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [rootMargin]);

  if (visible) return <>{children}</>;

  return <div ref={ref} style={{ minHeight }} />;
};
